import React from 'react';
import flex from '@/css/module/flex.css';
import AjaxSpinner from '@/components/AjaxSpinner';
import { EMAIL, SLACK } from '@/util/AlertRef';
import { Button, Sticker, TrevorIcon } from '@trevor/ui-components';
import css from '@/css/module/insights.css';
import {
  DashboardItemOptionContainer,
  InfoContainer,
  InfoItemContainer,
  TitleContainer,
} from '@/views/DashboardList/Dashboards.styles';
import { Permission } from '@/permissions/Permission';
import ShareIcon from '@/components/ShareIcon';
import DropdownButton from '@/components/DropdownButton';
import MultiDropdown from '@/components/MultiDropdown';
import DropdownItem from '@/util/DropdownItem';
import SearchInput from '@/components/SearchInput';
import { ShowMoreButton } from '@/components/ShowMoreButton';
import GenerateDashboards from '@/components/dashboards/GenerateDashboards';
import { get } from '@/util/helpers/ajax-helpers';
import { safeOnClick } from '@/util/helpers/event-helpers';
import { refreshFrequency } from '@/util/helpers/time-helpers';
import { TrevorLink } from '@/components/TrevorLink';

const RESULTS_TO_SHOW = 25;

export class DashboardList_LEGACY extends React.Component {
  state = {
    search: '',
    metas: {}, // { dashboardId -> [{ destination, emailRecipients, slackChannel }] }
    resultsToShow: RESULTS_TO_SHOW,
  };

  componentDidMount() {
    this.props.onLoadDashboards();
    this.loadSchedules();
  }

  loadSchedules = () =>
    // load tags for each dashboards that show which schedules have been set up
    get('loading dashboard snapshots', '/dashboards/schedules').then((json) => {
      const metas = {};
      json.schedules.map((s) => {
        const schedules = metas[s.dashboardId] || [];
        schedules.push({
          destination: s.destination,
          emailRecipients: s.destination !== EMAIL ? null : s.recipients,
          slackChannel: s.destination !== SLACK ? null : s.recipients[0],
        });
        metas[s.dashboardId] = schedules;
      });
      this.setState({
        metas,
      });
    });

  search = (value) =>
    this.setState({
      search: value,
    });

  handleShowMore = () =>
    this.setState((state) => ({
      resultsToShow: state.resultsToShow + RESULTS_TO_SHOW,
    }));

  dashboardGenerated = (d) => {
    // open the new dashboard
    this.props.onLoadDashboard(d)();
    // reload the dashboards list, so the new dashboard appears in the list
    this.props.onLoadDashboards();
  };

  tag(destination, recipient, index, onClick) {
    return (
      <div
        key={index}
        className={`${css.tag} user-clickable`}
        onClick={safeOnClick(onClick)}
      >
        <div className={css.tagIcon}>
          <TrevorIcon name={destination === SLACK ? 'slack' : 'mail'} />
        </div>
        <div className={css.tagContent}>{recipient}</div>
      </div>
    );
  }

  deliveryTag(meta, index, onClick) {
    if (meta.destination === SLACK) {
      return this.tag(SLACK, meta.slackChannel, index, onClick);
    }
    return meta.emailRecipients.map((mail, i) =>
      this.tag(EMAIL, mail, i, onClick),
    );
  }

  render() {
    const { metas, resultsToShow } = this.state;
    const {
      dashboards,
      isSelfHosted,
      datasourceId,
      onAddDashboard,
      onSetupDashboardPush,
      onEditDashboard,
      onDeleteDashboard,
    } = this.props;

    if (!dashboards) {
      return (
        <div className={flex.middle} style={{ height: '100vh' }}>
          <AjaxSpinner />
        </div>
      );
    }

    const filteredDashboards = dashboards
      .sort((d1, d2) => d1.name().localeCompare(d2.name()))
      .filter((d) => {
        const deliveries = metas[d.id()];
        // create a string containing all dashboard delivery recipients (so can be included as a search term)
        let metaRecipients = '';
        if (deliveries && deliveries.length > 0) {
          deliveries.forEach((delivery) => {
            if (delivery.destination === SLACK) {
              metaRecipients = `${metaRecipients} slack ${delivery.slackChannel}`;
            } else {
              const allEmails = delivery.emailRecipients.join(' ');
              metaRecipients = `${metaRecipients} email ${allEmails}`;
            }
          });
        }
        if (this.state.search.length < 2) {
          return true;
        }
        return !this.state.search
          .toLowerCase()
          .trim()
          .split(' ')
          .some((term) => {
            return (
              d.name().toLowerCase().indexOf(term) === -1 &&
              metaRecipients.toLowerCase().indexOf(term) === -1
            );
          });
      });

    const dashboardsToRender = filteredDashboards
      .slice(0, resultsToShow)
      .map((d) => {
        const updateFrequency = refreshFrequency(d);

        const publicDashboard = d.isPublic() ? (
          <Sticker iconName="eye" label="Public" />
        ) : null;

        const deliveryMeta = metas[d.id()];
        const dashboardWritePermission = `${
          this.props.datasourceId
        }.dashboards.${d.id()}.write`;
        return (
          <TrevorLink
            to={d.id()}
            className={`${css.insight} user-clickable`}
            key={d.id()}
            // onClick={safeOnClick(onLoadDashboard(d))}
          >
            <div
              className={`full-width ${flex.row} ${flex.justifyContentBetween} ${flex.alignItemsCenter}`}
            >
              <div className={flex.column}>
                <TitleContainer>
                  {d.name()}

                  <Permission permissionPath={dashboardWritePermission}>
                    {publicDashboard}
                    {d.isEmbedded && (
                      <Sticker type="success" iconName="web" label="Embedded" />
                    )}
                  </Permission>
                </TitleContainer>

                <InfoContainer>
                  <InfoItemContainer>
                    <TrevorIcon name="refresh" />
                    Refreshes {updateFrequency}
                  </InfoItemContainer>
                  {d.guests > 0 && (
                    <Permission permissionPath={dashboardWritePermission}>
                      <InfoItemContainer>
                        <TrevorIcon name="user" />
                        {d.guests} guest {d.guests === 1 ? 'user' : 'users'}
                      </InfoItemContainer>
                    </Permission>
                  )}
                </InfoContainer>

                {deliveryMeta && deliveryMeta.length > 0 ? (
                  <div
                    className={`full-width margin-top-15 ${flex.flex} ${flex.wrap}`}
                  >
                    {deliveryMeta.map((meta, i) =>
                      this.deliveryTag(
                        meta,
                        i,
                        onSetupDashboardPush(d, null, this.loadSchedules),
                      ),
                    )}
                  </div>
                ) : null}
              </div>
              <Permission permissionPath={dashboardWritePermission}>
                <DashboardItemOptionContainer>
                  {isSelfHosted ? null : (
                    <ShareIcon
                      place="top"
                      id="email"
                      tooltip="Deliver dashboard snapshot to Email"
                      onClick={safeOnClick(
                        onSetupDashboardPush(d, EMAIL, this.loadSchedules),
                      )}
                    >
                      <TrevorIcon name="mail" />
                    </ShareIcon>
                  )}
                  {isSelfHosted ? null : (
                    <ShareIcon
                      place="top"
                      id="slack"
                      tooltip="Deliver dashboard snapshot to Slack"
                      onClick={safeOnClick(
                        onSetupDashboardPush(d, SLACK, this.loadSchedules),
                      )}
                    >
                      <TrevorIcon name="slack" />
                    </ShareIcon>
                  )}
                  <DropdownButton
                    contentBuilder={() => (
                      <MultiDropdown
                        items={[
                          DropdownItem.action(
                            <span>Edit settings</span>,
                            onEditDashboard(d),
                          ),
                          DropdownItem.dropdownWithConfirmation(
                            <span>Delete dashboard</span>,
                            onDeleteDashboard(d),
                          ),
                        ]}
                      />
                    )}
                  >
                    <Button
                      size="small"
                      variant="secondary"
                      icon={<TrevorIcon name="menu-vertical" />}
                    />
                  </DropdownButton>
                </DashboardItemOptionContainer>
              </Permission>
            </div>
          </TrevorLink>
        );
      });

    const showShowMoreButton =
      dashboardsToRender.length > 0 &&
      filteredDashboards.length > resultsToShow;

    return (
      <div>
        <div className={css.aboveControl}>
          <div className={`${css.title} ${flex.justifyContentBetween}`}>
            <h1>Dashboards</h1>
            <Permission permissionPath={`${datasourceId}.write`}>
              <Button onClick={onAddDashboard}>Create new dashboard</Button>
            </Permission>
          </div>
        </div>
        <div
          className={css.controlContainer}
          style={{ top: '110px', paddingTop: '0px' }}
        >
          <div className={css.viewsControl}>
            <SearchInput
              value={this.state.search}
              onSearch={this.search}
              placeholder="Search dashboards"
            />
          </div>
        </div>
        <div className={css.dashboardsControl}>
          <div className={css.insightsContainer}>
            {dashboards.length === 0 && (
              <div className="padding-20">
                <div>
                  You have <b>0</b> dashboards. Create your first by clicking
                  the 'Create new dashboard' button above.
                </div>
              </div>
            )}
            {dashboardsToRender}
            {dashboards.length > 0 && dashboards.length === 0 && (
              <div className="padding-20">
                <span>
                  0 dashboards match your <b>search</b>. Clear the search{' '}
                  <b>above</b> to see all.
                </span>
              </div>
            )}
            {showShowMoreButton && (
              <ShowMoreButton onClick={this.handleShowMore} />
            )}
          </div>
          <GenerateDashboards onGenerated={this.dashboardGenerated} />
        </div>
      </div>
    );
  }
}
